// @erp/api-client — the typed edge of the lockstep monorepo (ADR-001 §4.2):
// hc<AppType>() over the gateway's exported route types, plus the one
// error-unwrapping discipline every portal shares. Isomorphic (browser +
// worker); no solid imports here.
//
// M0 SKELETON: the gateway's AppType lands in M1 — until then this package
// carries the client mechanics only. Usage once M1 exists:
//   import type { AppType } from '@erp/gateway';   // erp-api's exported type
//   const client = createApiClient<AppType>('/');
//   const loans = await unwrap<LoanPage>(client.api.lib.loans.$get({ query }));
import { hc } from "hono/client";
import type { AppErrorShape, ErrCode } from "@erp/core-schemas";

export type { InferRequestType, InferResponseType, ClientResponse } from "hono/client";

/**
 * hc with portal defaults: cookies ride along (the opaque session cookie is
 * the ONLY credential — DEV-GUIDE-005; no Authorization headers on the
 * browser path).
 */
export const createApiClient: typeof hc = (baseUrl, options) =>
  hc(baseUrl, { ...options, init: { credentials: "include", ...options?.init } });

/** The client-side face of AppErrorShape — code stays the typed ErrCode union. */
export class ApiError extends Error {
  constructor(
    public code: ErrCode,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Query-retry predicate (DEV-GUIDE-002 §3: never retry auth failures into a loop). */
export const isAuthError = (e: unknown): boolean =>
  e instanceof ApiError && (e.status === 401 || e.code === "UNAUTHORIZED");

interface ResponseLike {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

/**
 * Resolve a ClientResponse to its JSON, or throw ApiError parsed from the
 * wire's { error: { code, message } }. Non-JSON error bodies (CDN 502s and
 * friends) degrade to INTERNAL with the status attached — never a JSON parse
 * crash in the caller.
 */
export async function unwrap<T>(resOrPromise: ResponseLike | Promise<ResponseLike>): Promise<T> {
  const res = await resOrPromise;
  if (res.ok) {
    try {
      return (await res.json()) as T;
    } catch {
      // 200 with a non-JSON body (captive portal, misrouted HTML) — still an ApiError, never a raw SyntaxError
      throw new ApiError("INTERNAL", `Invalid JSON response (${res.status})`, res.status);
    }
  }
  let code: ErrCode = "INTERNAL";
  let message = `Request failed (${res.status})`;
  try {
    const body = (await res.json()) as Partial<AppErrorShape>;
    if (body?.error?.code) {
      code = body.error.code;
      // a code without a message keeps the status fallback — '' helps nobody
      if (typeof body.error.message === "string" && body.error.message) message = body.error.message;
    }
  } catch {
    // non-JSON error body — keep the generic status message
  }
  throw new ApiError(code, message, res.status);
}
